import React, { useEffect, useRef, useState } from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { CircularProgress } from '@material-ui/core';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import Input from '~/components/Form/Input';
import Select from '~/components/Form/Select';
import { useAuth } from '~/hooks/auth';
import getValidationErrors from '~/pages/utils/getValidationErrors';
import history from '~/services/history';
import { IState } from '~/store';
import {
  getDataListRequest,
  listDataListRequest,
  saveDataListRequest,
} from '~/store/modules/dataList/actions';
import { IDataList } from '~/store/modules/dataList/types';

import MaterialLayout from './Layout/MaterialLayout';
import useStyles from './styles';
import { Title, Container } from './styles';

interface EditDataListFormData {
  id: string | null;
  name: string;
  form_field_id: string;
  form_field_label: string;
  parent_id: string;
  responsible: string;
}

const EditDataList: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const [parent, setParent] = useState<IDataList | null>(null);

  const loadingGetDataList = useSelector(
    (state: IState) => state.dataList.loadingGetDataList,
  );
  const loadingSave = useSelector(
    (state: IState) => state.dataList.loadingSave,
  );
  const dataLists = useSelector((state: IState) => state.dataList.dataLists);
  const dataList = useSelector((state: IState) => state.dataList.dataList);

  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const dispatch = useDispatch();

  const classes = useStyles();

  const loadDataLists = () => {
    dispatch(listDataListRequest('', 1, 100000, 'asc', 'name'));
  };

  const handleSubmit: SubmitHandler<EditDataListFormData> = async (data) => {
    try {
      formRef.current?.setErrors({});
      const schema = Yup.object().shape({
        name: Yup.string()
          .required('Campo obrigatório.')
          .matches(
            /^[A-ZÀ-Ú0-9 _]+$/i,
            'Campo deve conter apenas caracteres alfanuméricos.',
          ),
        form_field_label: Yup.string().required('Campo obrigatório.'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const responsibleId = user.personId;
      const active = true;
      const { name, form_field_id, form_field_label } = data;
      const parent_id = parent ? parent.id : null;

      dispatch(
        saveDataListRequest(
          id,
          name,
          form_field_id,
          form_field_label,
          responsibleId,
          parent_id,
          active,
        ),
      );
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);

        formRef.current?.setErrors(errors);
      }
    }
  };

  const handleBackHistory = () => {
    history.goBack();
  };

  const handleChangeNameInput = () => {
    const name = formRef.current?.getFieldValue('name');
    const newFormFieldId = name !== '' ? 'wedo_'.concat(camelize(name)) : '';
    formRef.current?.setFieldValue('form_field_id', newFormFieldId);
  };

  const camelize = (str: string) => {
    const normalizedStr = str.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    return normalizedStr
      .replace(/(?:^\w|[A-U]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      })
      .replace(/\s+/g, '');
  };

  useEffect(() => {
    loadDataLists();
    if (id) {
      dispatch(getDataListRequest(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (dataList) {
      formRef.current?.setData({
        name: dataList?.name,
        form_field_id: dataList?.form_field_id,
        form_field_label: dataList?.form_field_label,
      });

      setParent(dataList?.parent);
    }
  }, [dataList]);

  const handleChangeSelect = (newParent: any) => {
    setParent(newParent);
  };

  return (
    <Container>
      <Title onClick={handleBackHistory}>
        <IoIosArrowBack
          style={{ background: '#fff', color: 'red', borderRadius: '50%' }}
          size={16}
        />
        <span>Voltar para cadastros</span>
      </Title>

      <h1>{id ? 'Editar Cadastro' : 'Novo Cadastro'}</h1>

      <div
        style={{
          padding: '16px',
          borderRadius: '5px',
        }}
      >
        <Form ref={formRef} onSubmit={handleSubmit}>
          <h1>Informações Gerais</h1>

          {!loadingGetDataList && (
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Input
                  name="name"
                  placeholder="Nome do cadastro"
                  label="Nome do cadastro"
                  onChange={handleChangeNameInput}
                  autoFocus
                />
              </Grid>
              <Grid item xs={6}>
                <Input
                  disabled
                  name="form_field_id"
                  placeholder="ID do formulário"
                  label="ID do formulário"
                />
              </Grid>
              <Grid item xs={6}>
                <Input
                  name="form_field_label"
                  placeholder="Título do campo"
                  label="Título do campo"
                />
              </Grid>
              <Grid item xs={6}>
                <Select
                  optional
                  name="parent_id"
                  placeholder="Selecione a lista relacionada"
                  label="Lista relacionada"
                  isClearable
                  backspaceRemovesValue
                  getOptionLabel={(data) => data.name}
                  getOptionValue={(data) => data.id}
                  options={dataLists.filter(
                    (currentDataList) => currentDataList.id !== dataList?.id,
                  )}
                  value={parent}
                  onChange={handleChangeSelect}
                />
              </Grid>
            </Grid>
          )}

          <div className={classes.buttons}>
            <Button onClick={handleBackHistory} className={classes.button}>
              Cancelar
            </Button>
            <div className={classes.wrapper}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
              >
                {loadingSave ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Salvar'
                )}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </Container>
  );
};

export default EditDataList;
